import { useMemo } from "react";
import { MapContainer, TileLayer, CircleMarker, Circle, Popup, Marker, Polygon } from "react-leaflet";
import L from "leaflet";
import { cellToBoundary } from "h3-js";
import "leaflet/dist/leaflet.css";

// Fix Leaflet's default icon path in bundlers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const JOB_COLOR = {
  dispatching: "#10b981",
  filled: "#3b82f6",
  failed: "#ef4444",
  closed: "#94a3b8",
};
const TILE_ATTR = '&copy; OpenStreetMap contributors';
const TILE_URL = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
const MAP_STYLE = { height: "100%", width: "100%" };
const WORKER_LIVE_STYLE = { color: "#059669", fillColor: "#10b981", fillOpacity: 0.9, weight: 2 };
const WORKER_HOME_STYLE = { color: "#64748b", fillColor: "#94a3b8", fillOpacity: 0.9, weight: 2 };

export default function DispatchMap({ jobs = [], workers = [], heatmap = null, height = 420 }) {
  const items = useMemo(() => {
    const j = (jobs || []).filter((x) => x.lat != null && x.lon != null);
    const w = (workers || []).filter((x) => x.lat != null && x.lon != null);
    return { j, w };
  }, [jobs, workers]);

  // Pre-compute heatmap polygons + colour scale (emerald→amber→red density)
  const hexes = useMemo(() => {
    if (!heatmap || !heatmap.cells?.length) return [];
    const max = Math.max(1, heatmap.max || 1);
    return heatmap.cells.map((c) => {
      let ring;
      try { ring = cellToBoundary(c.cell, false); } // [[lat, lng], ...]
      catch { return null; }
      const density = c.count / max;
      // Scale opacity 0.15..0.5, colour green→amber→red
      const hue = 140 - density * 140; // 140=emerald, 0=red
      const color = `hsl(${hue}, 70%, 45%)`;
      return { cell: c.cell, count: c.count, live: c.live, home: c.home, ring, color, opacity: 0.15 + density * 0.35 };
    }).filter(Boolean);
  }, [heatmap]);

  // Center: first live job if any, else first worker, else UK middle
  const center = useMemo(() => {
    if (items.j.length) return [items.j[0].lat, items.j[0].lon];
    if (items.w.length) return [items.w[0].lat, items.w[0].lon];
    return [54.5, -2.5];
  }, [items]);

  const zoom = items.j.length || items.w.length ? 10 : 5;

  return (
    <div data-testid="dispatch-map" className="card-tactile overflow-hidden" style={{ height }}>
      <MapContainer center={center} zoom={zoom} scrollWheelZoom={false} style={MAP_STYLE}>
        <TileLayer attribution={TILE_ATTR} url={TILE_URL} />

        {/* Heatmap hexes — draw before markers so pins sit on top */}
        {hexes.map((h) => (
          <Polygon
            key={`hex-${h.cell}`}
            positions={h.ring}
            pathOptions={{ color: h.color, weight: 1, fillColor: h.color, fillOpacity: h.opacity }}
          >
            <Popup>
              <div className="text-sm">
                <div className="font-bold">Supply density: {h.count} worker{h.count === 1 ? "" : "s"}</div>
                <div className="text-xs text-slate-600">Live GPS: {h.live} · Home: {h.home}</div>
                <div className="text-[10px] font-mono text-slate-400 mt-1">{h.cell}</div>
              </div>
            </Popup>
          </Polygon>
        ))}

        {items.j.map((j) => (
          <Circle
            key={`c-${j.id}`}
            center={[j.lat, j.lon]}
            radius={(j.max_radius_km || 25) * 1000}
            pathOptions={{ color: JOB_COLOR[j.dispatch_status] || "#64748b", weight: 1.5, fillOpacity: 0.08, interactive: false }}
          />
        ))}
        {items.j.map((j) => (
          <Marker key={`m-${j.id}`} position={[j.lat, j.lon]}>
            <Popup>
              <div className="text-sm">
                <div className="font-bold">{j.title}</div>
                <div>{j.location} · {j.postcode}</div>
                <div>{j.date} · {j.start_time}–{j.end_time}</div>
                <div>£{j.pay_rate}/hr · {j.staff_booked || 0}/{j.staff_required} booked</div>
                <div className="mt-1 text-xs uppercase font-bold" style={{ color: JOB_COLOR[j.dispatch_status] }}>{j.dispatch_status}</div>
              </div>
            </Popup>
          </Marker>
        ))}
        {items.w.map((w) => (
          <CircleMarker
            key={`w-${w.user_id}`}
            center={[w.lat, w.lon]}
            radius={7}
            pathOptions={w.source === "live" ? WORKER_LIVE_STYLE : WORKER_HOME_STYLE}
          >
            <Popup>
              <div className="text-sm">
                <div className="font-bold">{w.first_name} {w.last_name || ""}</div>
                <div>Status: {w.status}</div>
                <div>Source: {w.source}</div>
                {w.updated_at && <div className="text-xs text-slate-500">Updated {new Date(w.updated_at).toLocaleString()}</div>}
              </div>
            </Popup>
          </CircleMarker>
        ))}
      </MapContainer>
    </div>
  );
}
